import React from 'react';
import { Box, Typography, Container } from '@mui/material';
import { motion } from 'framer-motion';
import { viewportOnce, stagger, staggerItem, ease } from '../motion';
import { skillGroups, marqueeTech } from '../content';
import SectionHeading from './SectionHeading';
import Marquee from './Marquee';

const Skills: React.FC = () => (
  <Box component="section" id="skills" sx={{ py: { xs: 10, md: 18 }, position: 'relative' }}>
    <Container maxWidth="lg" sx={{ px: { xs: 3, md: 6 } }}>
      <SectionHeading
        index="02"
        eyebrow="skills"
        title="What I work with"
        subtitle="A toolkit refined through real projects — chosen for fit, not novelty."
      />

      <motion.div
        variants={stagger(0.08)}
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
            gap: { xs: 2.5, md: 3 },
          }}
        >
          {skillGroups.map((group) => (
            <motion.div key={group.title} variants={staggerItem}>
              <Box
                sx={{
                  height: '100%',
                  p: { xs: 3, md: 3.5 },
                  borderRadius: 3,
                  border: (t) => `1px solid ${t.palette.divider}`,
                  backgroundColor: 'background.paper',
                  transition: 'border-color 0.35s ease',
                  '&:hover': { borderColor: (t) => `${t.palette.primary.main}40` },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: (t) => t.typography.button.fontFamily,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.12em',
                    color: 'text.secondary',
                    mb: 2.5,
                    pb: 2,
                    borderBottom: (t) => `1px solid ${t.palette.divider}`,
                  }}
                >
                  {group.title}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {group.items.map((item) => (
                    <Box
                      key={item}
                      sx={{
                        fontFamily: (t) => t.typography.button.fontFamily,
                        fontSize: '0.8rem',
                        color: 'text.primary',
                        px: 1.5,
                        py: 0.75,
                        borderRadius: 1.5,
                        border: (t) => `1px solid ${t.palette.divider}`,
                        backgroundColor: 'rgba(255,255,255,0.02)',
                        transition: 'all 0.25s ease',
                        cursor: 'default',
                        '&:hover': {
                          color: 'primary.main',
                          borderColor: (t) => `${t.palette.primary.main}66`,
                        },
                      }}
                    >
                      {item}
                    </Box>
                  ))}
                </Box>
              </Box>
            </motion.div>
          ))}
        </Box>
      </motion.div>
    </Container>

    {/* Full-width slow marquee strip */}
    <motion.div
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={viewportOnce}
      transition={{ duration: 0.8, ease: ease.out }}
    >
      <Box
        sx={{
          mt: { xs: 7, md: 11 },
          py: { xs: 2, md: 3 },
          borderTop: (t) => `1px solid ${t.palette.divider}`,
          borderBottom: (t) => `1px solid ${t.palette.divider}`,
        }}
      >
        <Marquee items={marqueeTech} />
      </Box>
    </motion.div>
  </Box>
);

export default Skills;
